import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../contexts/auth";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { ChevronDownIcon, InformationCircleIcon } from "@heroicons/react/20/solid";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";

import { getReportAttendanceWorkingAreas, getReportAttendanceWorkingAreaDetail } from "../services/api_reports";
import { exportAttendanceWorkingAreaReports, exportAttendanceWorkingAreaCsvReports } from "../services/api_extractions";
import { estraiMessaggiErrore } from "../services/utils";

import ListBoxPageSize from "./listbox_pase_size";
import PrettyPagination from "./pretty_pagination";


function WorkingAreaRow({ token, row, startDate, endDate }) {
    const [open, setOpen] = useState(false);
    const [employeeDetails, setEmployeeDetails] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [errorDetails, setErrorDetails] = useState("");
    const [detailsLoaded, setDetailsLoaded] = useState(false);

    const detailController = useRef(null);

    const formatHours = (value) => {
        if (value === null || value === undefined || value === "") return "0";

        const numericValue = Number(value);

        if (Number.isNaN(numericValue)) return value;

        return parseFloat(numericValue.toFixed(2));
    };

    const fetchEmployeeDetails = async () => {
        const workingAreaId = row?.working_area?.id;

        if (!workingAreaId) return;

        setLoadingDetails(true);
        setErrorDetails("");

        detailController.current?.abort();
        detailController.current = new AbortController();
        const signal = detailController.current.signal;

        try {
            const response = await getReportAttendanceWorkingAreaDetail(
                token,
                { startDate, endDate },
                1,
                100,
                workingAreaId,
                signal,
                "attendance"
            );

            setEmployeeDetails(Array.isArray(response) ? response : response?.results || []);

            setDetailsLoaded(true);
        } catch (error) {
            if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
                console.error("Errore caricamento dettaglio area di lavoro:", error);
                setErrorDetails(estraiMessaggiErrore(error) || "Errore nel caricamento dei dettagli");
            }
        } finally {
            if (!signal.aborted) setLoadingDetails(false);
        }
    };

    const handleToggle = () => {
        const nextOpen = !open;

        setOpen(nextOpen);

        if (nextOpen && !detailsLoaded) fetchEmployeeDetails();
    };

    useEffect(() => {
        setOpen(false);
        setEmployeeDetails([]);
        setDetailsLoaded(false);
        setErrorDetails("");

        detailController.current?.abort();
    }, [startDate, endDate, row?.working_area?.id]);


    useEffect(() => {
        return () => {detailController.current?.abort();};
    }, []);

    return (
        <>
            <TableRow sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.04)' },}} onClick={handleToggle}>
                <TableCell sx={{ width: 48 }}>
                    <IconButton aria-label={open ? "Chiudi dettaglio" : "Apri dettaglio"} size="small" onClick={(event) => {event.stopPropagation(); handleToggle();}}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell component="th" scope="row">{row.working_area?.name || "-"}</TableCell>
                <TableCell align="right">{formatHours(row.assigned_working_area_hours)}</TableCell>
                <TableCell align="right">{formatHours(row.effective_hours)}</TableCell>
            </TableRow>

            <TableRow>
                <TableCell colSpan={4} sx={{ paddingBottom: 0, paddingTop: 0, backgroundColor: "rgb(238 242 255)"}}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 1 }}>
                            <Typography component="div" className="mb-2 font-semibold uppercase text-indigo-800">
                                Dettaglio dipendenti
                            </Typography>

                            {loadingDetails ? (
                                <div className="flex justify-center py-5"><CircularProgress size={28} /></div>
                            ) : errorDetails ? (
                                <Alert severity="error">{errorDetails}</Alert>
                            ) : (
                                <Table size="small" aria-label="dettaglio dipendenti area di lavoro">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell align="left">Dipendente</TableCell>
                                            <TableCell align="right">Ore assegnate</TableCell>
                                            <TableCell align="right">Ore rilevate</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {employeeDetails.length > 0 ? (
                                            employeeDetails.map((detail) => {
                                                const employeeName = [detail.employee?.last_name, detail.employee?.first_name].filter(Boolean).join(" ");
                                                const employeeLabel = employeeName || detail.employee?.cf || "-";

                                                return (
                                                    <TableRow key={detail.employee.id}>
                                                        <TableCell align="left">{employeeLabel}</TableCell>
                                                        <TableCell align="right">{formatHours(detail.assigned_working_area_hours)}</TableCell>
                                                        <TableCell align="right">{formatHours(detail.effective_hours)}</TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={3} align="left" sx={{ py: 1 }}>Nessun dettaglio disponibile</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}



export default function CollapsibleWorkingAreaTable({ selectedWorkingAreaId, startDate, endDate }) {
    const { token } = useContext(AuthContext);

    const [workingAreas, setWorkingAreas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [totalResult, setTotalResult] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [showInfo, setShowInfo] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    

    const controller = useRef(null);
    const totalPages = Math.ceil(totalResult / pageSize);

    useEffect(() => {
        setPage(1);
    }, [selectedWorkingAreaId, startDate, endDate]);

    useEffect(() => {
        const fetchWorkingAreas = async () => {
            if (!token || !startDate || !endDate) return;

            setIsLoading(true);
            setError("");

            controller.current?.abort();
            controller.current = new AbortController();

            const signal = controller.current.signal;

            try {
                const selectedWorkingAreaParam = selectedWorkingAreaId !== null && selectedWorkingAreaId !== undefined
                    ? selectedWorkingAreaId
                    : undefined;

                const response = await getReportAttendanceWorkingAreas(
                    token,
                    { startDate, endDate },
                    selectedWorkingAreaParam,
                    [],
                    page,
                    pageSize,
                    signal,
                    "attendance"
                );

                setWorkingAreas(response?.results || []);
                setTotalResult(response?.count || 0);
            } catch (error) {
                if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
                    console.error("Errore caricamento statistiche per area di lavoro:", error);
                    setError(estraiMessaggiErrore(error) || "Errore nel caricamento delle statistiche per area di lavoro");
                }
            } finally {
                if (!signal.aborted) setIsLoading(false);
            }
        };

        fetchWorkingAreas();

        return () => {
            controller.current?.abort();
        };
    }, [token, startDate, endDate, page, pageSize, selectedWorkingAreaId]);

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(Number(newPageSize));
        setPage(1);
    };

    const handleExportReport = async () => {
        if (!startDate || !endDate || isExporting) return;

        setIsExporting(true);

        try {
            const { status, data } = await exportAttendanceWorkingAreaReports(
                token,
                startDate,
                endDate,
                selectedWorkingAreaId
            );

            const detail = String(data?.detail || "");
            const alreadyExists = status === 200 && (detail.toLowerCase().includes("già") || detail.toLowerCase().includes("presente"));

            await Swal.fire({
                icon: alreadyExists ? "warning" : "success",
                title: alreadyExists ? "Esportazione già presente" : "Esportazione avviata",
                html: `
                    <div>
                        ${
                            alreadyExists
                                ? detail || "Esiste già un export per gli stessi parametri."
                                : "Il file verrà generato in background e sarà disponibile nella pagina download."
                        }
                        <br />
                        <a
                            href="/download_list"
                            style="color:#4f46e5; text-decoration:underline;"
                        >
                            Vai alla pagina download
                        </a>
                    </div>
                `,
                confirmButtonText: "Chiudi",
            });
        } catch (error) {
            console.error("Errore esportazione statistiche per area di lavoro:", error);

            const status = error?.response?.status;
            const detail = error?.response?.data?.detail;

            if (status === 404) {
                await Swal.fire({
                    icon: "info",
                    title: "Nessun dato trovato",
                    text: detail || "Nessun dato disponibile per il periodo selezionato.",
                    confirmButtonText: "Chiudi",
                });

                return;
            }

            await Swal.fire({
                icon: "error",
                title: "Errore",
                text: detail || estraiMessaggiErrore(error) || "Errore durante la richiesta di esportazione.",
            });
        } finally {
            setIsExporting(false);
        }
    };

    const handleExportReportCsv = async () => {
        if (!startDate || !endDate || isExporting) return;

        setIsExporting(true);

        try {
            const { status, data } = await exportAttendanceWorkingAreaCsvReports(
                token,
                startDate,
                endDate,
                selectedWorkingAreaId
            );

            const detail = String(data?.detail || "");
            const alreadyExists =
                status === 200 &&
                (detail.toLowerCase().includes("già") ||
                detail.toLowerCase().includes("presente"));

            await Swal.fire({
                icon: alreadyExists ? "warning" : "success",
                title: alreadyExists ? "Esportazione già presente" : "Esportazione avviata",
                html: `
                    <div>
                        ${
                            alreadyExists
                                ? detail || "Esiste già un export per gli stessi parametri."
                                : "Il file verrà generato in background e sarà disponibile nella pagina download."
                        }
                        <br />
                        <a
                            href="/download_list"
                            style="color:#4f46e5; text-decoration:underline;"
                        >
                            Vai alla pagina download
                        </a>
                    </div>
                `,
                confirmButtonText: "Chiudi",
            });

        } catch (error) {
            console.error("Errore esportazione report CSV:", error);

            const status = error?.response?.status;
            const detail = error?.response?.data?.detail;

            if (status === 404) {
                await Swal.fire({
                    icon: "info",
                    title: "Nessun dato trovato",
                    text: detail || "Nessun dato disponibile per il periodo selezionato.",
                    confirmButtonText: "Chiudi",
                });
                return;
            }

            await Swal.fire({
                icon: "error",
                title: "Errore",
                text: detail || estraiMessaggiErrore(error) || "Errore durante la richiesta di esportazione.",
            });

        } finally {
            setIsExporting(false);
        }
    };


    if (isLoading) {return (<div className="flex justify-center py-10"><CircularProgress /></div>);}

    if (error) {return (<Alert severity="error">{error}</Alert>);}

    return (
        <>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-b-lg border-x border-b border-gray-200 bg-gray-50 px-3 py-2">
                <ListBoxPageSize value={pageSize} onChange={handlePageSizeChange} />

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setShowExportDropdown((prev) => !prev)}
                        disabled={isExporting || !startDate || !endDate}
                        aria-expanded={showExportDropdown}
                        className="inline-flex h-8 items-center justify-center gap-2 rounded-md bg-indigo-600 px-3 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <ArrowDownTrayIcon className="h-4 w-4" />

                        {isExporting ? "Avvio export..." : "Esporta report"}

                        <ChevronDownIcon
                            className={`h-4 w-4 transition-transform ${
                                showExportDropdown ? "rotate-180" : ""
                            }`}
                        />
                    </button>

                    {showExportDropdown && (
                        <div className="absolute right-0 z-50 mt-2 w-48 overflow-hidden rounded-lg border border-gray-200 bg-white py-1 shadow-lg">

                            <button
                                type="button"
                                disabled={isExporting}
                                onClick={() => {
                                    setShowExportDropdown(false);
                                    handleExportReport();
                                }}
                                className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50"
                            >
                                Report Excel
                            </button>

                            <button
                                type="button"
                                disabled={isExporting}
                                onClick={() => {
                                    setShowExportDropdown(false);
                                    handleExportReportCsv();
                                }}
                                className="block w-full border-t border-gray-100 px-4 py-2 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 disabled:opacity-50"
                            >
                                Report CSV
                            </button>

                        </div>
                    )}
                </div>
            </div>

            <div className="mb-4 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                <button
                    type="button"
                    onClick={() => setShowInfo((prev) => !prev)}
                    aria-expanded={showInfo}
                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-indigo-50/50 sm:px-5"
                >
                    <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700"><InformationCircleIcon className="h-5 w-5" /></div>
                        <div className="min-w-0">
                            <p className="text-sm font-semibold uppercase text-indigo-800">Istruzioni di lettura</p>
                            <p className="mt-0.5 text-xs text-gray-500">Consulta il significato delle ore raggruppate per area di lavoro</p>
                        </div>
                    </div>

                    <ChevronDownIcon className={`h-5 w-5 shrink-0 text-gray-400 transition-transform duration-200 ${showInfo ? "rotate-180" : "" }`} />
                </button>

                {showInfo && (
                    <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-4 sm:px-5">
                        <div className="space-y-3 text-sm leading-6 text-gray-700">
                            <p>La tabella confronta le ore assegnate alle aree di lavoro con le ore rilevate tramite i giustificativi identificativi delle aree.</p>

                            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Ore assegnate</p>
                                    <p className="mt-1">Totale delle ore dei time slot pianificati e associati all’area di lavoro nel periodo selezionato.</p>
                                </div>

                                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Ore rilevate</p>
                                    <p className="mt-1">Totale delle ore risultanti dai giustificativi area attribuiti all’area di lavoro.</p>
                                </div>

                                <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Dettaglio dipendenti</p>
                                    <p className="mt-1">Aprendo una riga puoi verificare ore assegnate e rilevate suddivise per dipendente.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-md">
                <TableContainer component={Paper} elevation={0}>
                    <Table aria-label="statistiche aree di lavoro">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 48 }} />
                                <TableCell align="left">Area di lavoro</TableCell>
                                <TableCell align="right">Ore assegnate</TableCell>
                                <TableCell align="right">Ore rilevate</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {workingAreas.length > 0 ? (
                                workingAreas.map((row) => (
                                    <WorkingAreaRow
                                        key={row.working_area.id}
                                        token={token}
                                        row={row}
                                        startDate={startDate}
                                        endDate={endDate}
                                    />
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 5 }}>Nessun dato disponibile per il periodo selezionato</TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            <PrettyPagination
                page={page}
                totalPages={totalPages}
                totalCount={totalResult}
                onChange={(newPage) => setPage(newPage)}
                labelSingolare="area"
                labelPlurale="aree"
                className="rounded-b-xl border-x border-b border-gray-200 bg-white px-4 py-4 shadow-sm"
            />
        </>
    );
}